import Link from 'next/link';
import React, { FC, useState } from 'react';
import {
    IoArchiveOutline,
    IoAt,
    IoCartOutline,
    IoChatbubbleOutline,
    IoCheckmark,
    IoCheckmarkCircleOutline,
    IoCloseCircleOutline,
    IoEllipsisHorizontal,
    IoHandLeftOutline,
    IoHappyOutline,
    IoHeadsetOutline,
    IoNotificationsOutline,
    IoPersonAddOutline,
    IoQrCodeOutline,
    IoSparklesOutline,
    IoWalletOutline,
} from 'react-icons/io5';
import { PiHandshake } from 'react-icons/pi';

import { ActionIcon, Box, Button, Group, Menu, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';

import { InboxNotification, NotificationKey } from 'models';
import { NotificationHelpers } from 'helpers/NotificationHelpers';

import { Avatar } from 'components/avatar/Avatar';
import { useAction } from 'hooks/use-action';
import { DateService } from 'services/DateService';
import { NotificationService } from 'services/NotificationService';
import { RouterService } from 'services/RouterService';

import cx from './Notification.module.scss';

const ICON_BY_TYPE: Record<NotificationKey, React.ReactNode> = {
    [NotificationKey.DIAGRAM_CHAT_MESSAGE]: <IoChatbubbleOutline />,
    [NotificationKey.DIAGRAM_CHAT_CHANNEL]: <IoHeadsetOutline />,
    [NotificationKey.INTERCOM_MESSAGE]: <IoChatbubbleOutline />,
    [NotificationKey.DIAGRAM_CHAT_REACTION]: <IoHappyOutline />,
    [NotificationKey.INTERCOM_REACTION]: <IoHappyOutline />,
    [NotificationKey.ORDER_MESSAGE]: <IoChatbubbleOutline />,
    [NotificationKey.DIAGRAM_COMMENT_CREATE]: <IoChatbubbleOutline />,
    [NotificationKey.DIAGRAM_COMMENT_REPLY_USER]: <IoChatbubbleOutline />,
    [NotificationKey.DIAGRAM_COMMENT_REPLY_AI]: <IoSparklesOutline />,
    [NotificationKey.DIAGRAM_COMMENT_MENTION_USER]: <IoAt />,
    [NotificationKey.DIAGRAM_COMMENT_MENTION_MANUFACTURER]: <IoAt />,
    [NotificationKey.DIAGRAM_INVITE]: <IoPersonAddOutline />,
    [NotificationKey.USER_TEAM_INVITE]: <IoPersonAddOutline />,
    [NotificationKey.DIAGRAM_REQUESTACCESS]: <IoHandLeftOutline />,
    [NotificationKey.COMPANY_REQUEST_ACCESS]: <IoHandLeftOutline />,
    [NotificationKey.TEAM_REQUEST_ACCESS]: <IoHandLeftOutline />,
    [NotificationKey.COMPANY_GRANT_ACCESS]: <IoCheckmarkCircleOutline />,
    [NotificationKey.TEAM_GRANT_ACCESS]: <IoCheckmarkCircleOutline />,
    [NotificationKey.COMPANY_DENY_ACCESS]: <IoCloseCircleOutline />,
    [NotificationKey.TEAM_DENY_ACCESS]: <IoCloseCircleOutline />,
    [NotificationKey.COMPANY_ADDED_AS_PARTNER]: <PiHandshake />,
    [NotificationKey.COMPANY_NEW_EXHIBITOR_MATCH_LEAD]: <IoQrCodeOutline />,
    [NotificationKey.COMPANY_NEW_QR_SCAN]: <IoQrCodeOutline />,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_ESTIMATE]: <IoWalletOutline />,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_ACCEPT]: <IoWalletOutline />,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_DECLINE]: <IoWalletOutline />,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_CHARGE]: <IoWalletOutline />,
    [NotificationKey.ORDER_EVENT]: <IoCartOutline />,
    [NotificationKey.DESIGN_DUPLICATE]: <IoNotificationsOutline />,
};

const Notification: FC<{ notification: InboxNotification }> = ({ notification }) => {
    const isUnread = !notification.readAt;

    return (
        <Box
            component={Link}
            href={notification.app.url}
            onClick={() => {
                NotificationService.read(notification).then();
                NotificationService.closeInbox();
            }}
            className={cx.notification}
            data-notification={notification.id}
            data-unread={isUnread || undefined}
        >
            <Group gap="sm" wrap="nowrap" align="flex-start">
                <NotificationAvatar notification={notification} />

                <Stack gap={2} miw={0} flex={1}>
                    <Text
                        size="sm"
                        className={cx.notificationText}
                        dangerouslySetInnerHTML={{ __html: notification.app.text }}
                    />
                    <NotificationPreview notification={notification} />
                    <NotificationActions notification={notification} />
                </Stack>

                <Stack gap={6} align="flex-end" style={{ flexShrink: 0 }}>
                    <Text size="xs" c="dimmed" className={cx.date}>
                        {DateService.formatDistanceToNowShort(notification.createdAt)}
                    </Text>
                    <NotificationControls notification={notification} />
                </Stack>
            </Group>
        </Box>
    );
};

const NotificationAvatar: FC<{ notification: InboxNotification }> = ({ notification }) => {
    const icon = ICON_BY_TYPE[notification.type as NotificationKey] ?? <IoNotificationsOutline />;
    const sender = NotificationHelpers.getSender(notification);
    const { image } = notification.app;

    if (!sender && !image) {
        return (
            <ThemeIcon size={32} radius="xl" variant="light" color="gray" fz={16} style={{ flexShrink: 0 }}>
                {icon}
            </ThemeIcon>
        );
    }

    return (
        <Box className={cx.notificationAvatar}>
            {sender ? (
                <Avatar user={sender.user} company={sender.company} size={32} />
            ) : (
                <Avatar name=" " image={image} size={32} />
            )}
            <ThemeIcon className={cx.notificationBadge} size={16} radius="xl" variant="white" fz={10}>
                {icon}
            </ThemeIcon>
        </Box>
    );
};

const NotificationPreview: FC<{ notification: InboxNotification }> = ({ notification }) => {
    const { preview } = notification.app;

    return preview ? (
        <Text size="sm" c="dimmed" lineClamp={2}>
            {preview}
        </Text>
    ) : null;
};

const NotificationActions: FC<{ notification: InboxNotification }> = ({ notification }) => {
    const { button } = notification.app;

    return (
        <>
            {button?.label && button?.url && (
                <Button
                    component="span"
                    mt={4}
                    size="compact-xs"
                    radius="xs"
                    variant="default"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => RouterService.push(button.url)}
                >
                    {button.label}
                </Button>
            )}
        </>
    );
};

const NotificationControls: FC<{ notification: InboxNotification }> = ({ notification }) => {
    const isUnread = !notification.readAt;

    const [markAsRead, isMarking] = useAction(() => NotificationService.read(notification));
    const [archive, isArchiving] = useAction(() => NotificationService.archive(notification));
    const [isMenuOpened, setIsMenuOpened] = useState(false);
    const isBusy = isMarking || isArchiving || isMenuOpened;

    const runWithoutOpening = (action: () => Promise<unknown>) => (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        action().then();
    };

    return (
        <>
            {isUnread && <Box className={cx.unreadDot} data-hidden={isBusy || undefined} />}
            <Group gap={4} wrap="nowrap" className={cx.controls} data-busy={isBusy || undefined}>
                {isUnread && (
                    <Tooltip label="Mark as read" withArrow>
                        <ActionIcon
                            variant="filled"
                            color="green"
                            size="sm"
                            loading={isMarking}
                            disabled={isArchiving}
                            aria-label="Mark as read"
                            onClick={runWithoutOpening(markAsRead)}
                        >
                            <IoCheckmark size={14} />
                        </ActionIcon>
                    </Tooltip>
                )}
                <Menu position="bottom-end" offset={4} shadow="md" onChange={setIsMenuOpened}>
                    <Menu.Target>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            loading={isArchiving}
                            disabled={isMarking}
                            aria-label="More actions"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                        >
                            <IoEllipsisHorizontal size={14} />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
                        <Menu.Item leftSection={<IoArchiveOutline />} onClick={runWithoutOpening(archive)}>
                            Archive
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Group>
        </>
    );
};

export { Notification, NotificationAvatar, NotificationControls };
